import { useEffect, useState } from "react"
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material"
import { PasswordTextField } from "./PasswordField"
import { toast } from "sonner"
import {
  websiteAuthService,
  type ChangePasswordData,
} from "@/api/websiteAuthService"

type Props = { open: boolean; onClose: () => void }
type FieldErrors = Partial<Record<keyof ChangePasswordData, string>>
const emptyPasswords: ChangePasswordData = {
  current_password: "",
  new_password: "",
  new_password_confirmation: "",
}

export const ChangePasswordDialog = ({ open, onClose }: Props) => {
  const [passwords, setPasswords] = useState(emptyPasswords)
  const [errors, setErrors] = useState<FieldErrors>({})
  const [submitError, setSubmitError] = useState("")
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (!open) {
      setPasswords(emptyPasswords)
      setErrors({})
      setSubmitError("")
    }
  }, [open])

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    const validationErrors: FieldErrors = {}
    if (passwords.new_password !== passwords.new_password_confirmation) {
      validationErrors.new_password_confirmation =
        "The new passwords do not match."
    }
    if (passwords.new_password === passwords.current_password) {
      validationErrors.new_password =
        "Choose a different password from your current password."
    }
    setErrors(validationErrors)
    setSubmitError("")
    if (Object.keys(validationErrors).length > 0) return

    setIsSaving(true)
    try {
      await websiteAuthService.changePassword(passwords)
      toast.success("Password changed successfully")
      onClose()
    } catch (error: any) {
      if (error?.error && error.status === 422) {
        const fieldErrors: FieldErrors = {}
        for (const [field, messages] of Object.entries(error.error)) {
          if (field in emptyPasswords && Array.isArray(messages)) {
            fieldErrors[field as keyof ChangePasswordData] = messages[0]
          }
        }
        if (Object.keys(fieldErrors).length) {
          setErrors(fieldErrors)
        } else {
          setSubmitError(
            error?.message || "Unable to change password. Please try again."
          )
        }
      } else {
        setSubmitError(
          error?.message || "Unable to change password. Please try again."
        )
      }
    } finally {
      setIsSaving(false)
    }
  }

  const updatePassword = (field: keyof ChangePasswordData, value: string) => {
    setPasswords((previous) => ({ ...previous, [field]: value }))
    setErrors({})
    setSubmitError("")
  }

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!isSaving) onClose()
      }}
      maxWidth="xs"
      fullWidth
      aria-labelledby="change-password-title"
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle id="change-password-title">Change Password</DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            pt: "8px !important",
          }}
        >
          {submitError && <Alert severity="error">{submitError}</Alert>}
          <PasswordTextField
            label="Current password"
            type="password"
            autoComplete="current-password"
            autoFocus
            required
            fullWidth
            disabled={isSaving}
            value={passwords.current_password}
            onChange={(event) =>
              updatePassword("current_password", event.target.value)
            }
            error={!!errors.current_password}
            helperText={errors.current_password}
          />
          <PasswordTextField
            label="New password"
            type="password"
            autoComplete="new-password"
            required
            fullWidth
            disabled={isSaving}
            inputProps={{ minLength: 8 }}
            value={passwords.new_password}
            onChange={(event) =>
              updatePassword("new_password", event.target.value)
            }
            error={!!errors.new_password}
            helperText={errors.new_password || "At least 8 characters."}
          />
          <PasswordTextField
            label="Confirm new password"
            type="password"
            autoComplete="new-password"
            required
            fullWidth
            disabled={isSaving}
            inputProps={{ minLength: 8 }}
            value={passwords.new_password_confirmation}
            onChange={(event) =>
              updatePassword("new_password_confirmation", event.target.value)
            }
            error={!!errors.new_password_confirmation}
            helperText={errors.new_password_confirmation}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={isSaving}>
            {isSaving ? "Saving..." : "Update Password"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
